// frontend/src/components/builder/SchemaDrivenForm.tsx
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SchemaDrivenFormProps {
  schema: z.ZodObject<any>;
  defaultValues: Record<string, any>;
  onUpdate: (data: Record<string, any>) => void;
}

export default function SchemaDrivenForm({ schema, defaultValues, onUpdate }: SchemaDrivenFormProps) {
  const { register, handleSubmit, watch, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Reset the form if the default values change (i.e., a new component is selected)
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Watch for changes and call the onUpdate callback
  useEffect(() => {
    const subscription = watch((value) => {
      onUpdate(value);
    });
    return () => subscription.unsubscribe();
  }, [watch, onUpdate]);

  const onSubmit = (data: any) => {
    // This form is for live updates, but we can keep onSubmit for explicit save actions if needed.
    console.log('Form submitted (optional):', data);
  };

  const fields = Object.keys(schema.shape);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {fields.map((fieldName) => (
        <div key={fieldName} className="space-y-2">
          <Label htmlFor={fieldName} className="capitalize">{fieldName.replace(/_/g, ' ')}</Label>
          {/* This is a simple renderer. It can be expanded for different types. */}
          <Input
            id={fieldName}
            {...register(fieldName)}
            className="w-full"
          />
        </div>
      ))}
      {/* Optional: Add a submit button if live updates aren't enough */}
      {/* <Button type="submit" className="mt-4">Update</Button> */}
    </form>
  );
}
